// components/CarComparisonPage.tsx
"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import styles from "./ComparisonPage.module.scss";
import CompareCard from "../CompareCard";
import assetLoader, { s3UrlLoader } from "@utils/internalImageLoder";
import { toKebabCase } from "@utils/stringFormatters";
import ToggleSwitch from "@components/atoms/ToggleSwitch";
import { ComparisonData, IModelsData } from "@interfaces/comparison.types";
import AccentBar from "@components/atoms/AccentBar";
import { useIsMobile } from "@hooks/use-mobile";
import {
    addModelToComparisonSlug,
    extractCarSlugs,
    generateVariantQueryParams,
    getMakeModelPairs,
    createHeaderFromMakeModelPairs,
    updateComparisonSlug,
} from "@utils/comparison";
import ModalManager from "@components/molecules/ModalManager";
import Button from "@components/atoms/Button";
import AddToCompareModal from "@components/molecules/MMVSelectModal";
import ComparePriceCard from "@components/molecules/ComparePriceCard";
import { CarVariant } from "../../molecules/ComparisonVariantDetails";
import Spinner from "@components/atoms/LoaderSpinner/LoaderSpinner";
import { DEFAULT_CITY } from "@constants/global";
import ImageAtom from "@components/atoms/Image";
interface Props {
    data: ComparisonData[];
    slug: string;
    city: any;
    isLoggedIn: boolean;
    modelsDetails?: IModelsData[];
}

const MAX_COMPARISON_CARDS = 3;

export default function CarComparisonPage({ data, slug, city, isLoggedIn, modelsDetails }: Props) {
    const isMobile = useIsMobile();
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const modelsData = modelsDetails || [];
    const availableVariants = modelsData.map((model: any) => {
        const allVariants = model?.allVariantSlugs?.uniqueSortedVariants || [];
        return allVariants.map((variant: any) => ({
            ...variant,
            modelId: model.id,
        }));
    });

    // Move getSelectedVariants above its usage and ensure it always returns CarVariant[]
    const getSelectedVariants = (availableVariants: any[]): CarVariant[] => {
        if (!availableVariants?.length) return [];
        const selected = availableVariants.map((variantList, index) => {
            const slugFromQuery = searchParams?.get(`variant${index + 1}`);
            const match = variantList.find((variant: { slug: string | null; }) => variant.slug === slugFromQuery);
            return match || null; // fallback logic can be added
        });
        // Optional fallback to first available variant if query match fails
        const cleaned = selected.map((variant, index) =>
            variant || availableVariants[index]?.[0]
        );
        return cleaned.filter(Boolean);
    };

    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>(() => {
        const firstCategory = data[0]?.category;
        return firstCategory ? { [firstCategory]: true } : {};
    });
    const [isOnlyDiff, setIsOnlyDiff] = useState<boolean>(false);
    const [filteredData, setFilteredData] = useState<
        ComparisonData[] | null | undefined
    >(data);
    const [compareModelsList, setCompareModelsList] = useState<
        { make: string; model: string }[]
    >([]);
    const [comfirmationModal, setConfirmationModal] = useState<boolean>(false);
    const [selectedIndexToRemove, setSelectedIndexToRemove] = useState<
        number | null
    >(null);
    const [openMMVModal, setOpenMMVModal] = useState<boolean>(false);
    const [selectedVariants, setSelectedVariants] = useState<CarVariant[]>(getSelectedVariants(availableVariants));
    const [isEditIndex, setIsEditIndex] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    // Create header from actual brand and model names 
    const header = modelsData.length > 0
        ? createHeaderFromMakeModelPairs(
            modelsData.map(item => ({ 
                make: item?.brand?.name, 
                model: item?.model?.name 
            }))
          )
        : '';
        
    const cityName = city?.name || DEFAULT_CITY;

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    useEffect(() => {
        if (slug) {
            const list = getMakeModelPairs(slug);
            setCompareModelsList(list);
        }
    }, [slug]);

    useEffect(() => {
        const carSlugs = extractCarSlugs(slug);
        const hasAnySlugInQuery = ["variant1", "variant2", "variant3"].some((key) =>
            searchParams?.get(key)
        );
        if (!hasAnySlugInQuery && modelsData?.length) {
            let selected: CarVariant[] = [];
            const allSameSlug =
                carSlugs.length > 1 &&
                carSlugs.every((slug) => slug === carSlugs[0]);
            // if comparison happens between the same model slug
            if (allSameSlug) {
                selected = modelsData.map((model, index) => {
                    if (index === 1) return model?.allVariantSlugs?.baseVariant;
                    return model?.allVariantSlugs?.topVariant;
                }).filter((v): v is CarVariant => v !== undefined);
            } else {
                selected = modelsData
                    .map((model) => model?.allVariantSlugs?.topVariant)
                    .filter((v): v is CarVariant => v !== undefined);
            }
            setSelectedVariants(selected);
        }
    }, [modelsData]);

    const toggleAccordion = (category: string) => {
        setExpanded((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const onAddClick = () => {
        setOpenMMVModal(true);
        setIsEditIndex(null);
    };

    const onRemoveClick = (indexToRemove: number) => {
        setSelectedIndexToRemove(indexToRemove);
        setConfirmationModal(true);
    };
    useEffect(() => {
        setLoading(false);
    }, [modelsData]);
    const handleVariantChange = (index: number, variant: CarVariant) => {
        setLoading(true);
        const updated = [...selectedVariants];
        updated[index] = variant;
        setSelectedVariants(updated);

        const slugs: string[] = updated.map(v => v?.slug).filter(Boolean) as string[];

        const queryParams = generateVariantQueryParams(
            slugs.map(slug => ({ slug }))
        );

        const newUrl = `${pathname}?${queryParams}`;

        router.replace(newUrl);
    };

    const onEdit = (index: number) => {
        setIsEditIndex(index);
        setOpenMMVModal(true);
    }

    const confirmRemove = () => {
        if (selectedIndexToRemove === null) return;

        const updatedData = filteredData?.map((category) => {
            const updatedFeatures = category.features.map((feature) => {
                const valueEntries = Object.entries(feature.values);
                const filteredEntries = valueEntries.filter((_, i) => i !== selectedIndexToRemove);
                const updatedValues = Object.fromEntries(filteredEntries);
                return {
                    ...feature,
                    values: updatedValues,
                };
            });

            return {
                ...category,
                features: updatedFeatures,
            };
        });

        setFilteredData(updatedData);
        setSelectedVariants((prev) => prev.filter((_, i) => i !== selectedIndexToRemove));
        setCompareModelsList((prev) => prev.filter((_, i) => i !== selectedIndexToRemove));
        setConfirmationModal(false);
        setSelectedIndexToRemove(null);
    };

    const cancelRemove = () => {
        setConfirmationModal(false);
        setSelectedIndexToRemove(null);
    };

    const handleDiffFilter = (value: boolean) => {
        setIsOnlyDiff(value);

        if (!value) {
            // Show all data
            setFilteredData(data);
            // Expand first category if present
            const firstCategory = data[0]?.category;
            setExpanded(firstCategory ? { [firstCategory]: true } : {});
        } else {
            // Filter to only categories and features that have differences
            const filtered: ComparisonData[] = data
                .map((category): ComparisonData | null => {
                    const filteredFeatures = category.features.filter(f => f.hasDifference);
                    if (filteredFeatures.length > 0) {
                        return { ...category, features: filteredFeatures };
                    }
                    return null;
                })
                .filter((c): c is ComparisonData => c !== null); // <- type guard
            // remove nulls

            setFilteredData(filtered);
            // Expand first filtered category if present
            const firstFilteredCategory = filtered[0]?.category;
            setExpanded(firstFilteredCategory ? { [firstFilteredCategory]: true } : {});
        }
    };

    const handleMMVSelection = (
        brand: string,
        modelSlug: string,
        variantSlug: string,
        selectedVariant?: CarVariant | any
    ) => {
        setLoading(true);
        let queryParams = "";
        let newSlug = slug;
        const updatedVariants = [...selectedVariants];

        if (isEditIndex != null) {
            // Replace model in slug
            newSlug = updateComparisonSlug(slug, isEditIndex, modelSlug);

            // Update the selected variant at the edited index
            if (selectedVariant) {
                updatedVariants[isEditIndex] = selectedVariant;
            }

            queryParams = generateVariantQueryParams(
                updatedVariants.map((v) => ({ slug: v?.slug }))
            );
        } else {
            // Add new model to slug
            newSlug = addModelToComparisonSlug(slug, modelSlug);
            const variantsArr = [
                { slug: selectedVariants[0]?.slug },
                { slug: selectedVariants[1]?.slug },
                { slug: variantSlug },
            ].filter(Boolean); // remove undefined

            if (variantsArr.length) {
                queryParams = generateVariantQueryParams(variantsArr);
            }
        }

        router.push(`/compare/${newSlug}?${queryParams}`);
    };

    const closeConfirmationModal = () => {
        setLoading(true);
        setConfirmationModal(false);
    };
    return (
        <>
            {loading && (
                <div className={styles.loadingWrapper}>
                    <Spinner />
                </div>
            )}
            <ModalManager
                modalHeaderClass={styles.modalHeader}
                isOpen={!!comfirmationModal}
                onClose={closeConfirmationModal }
                title={
                    <div className={styles.modalTitleWrapper}>
                        <ImageAtom src={`${assetLoader()}/triangle-warning.png`} alt="triangle" height={24} width={24} />
                    </div>
                }
            >
                <div className={styles.modalContentWrapper}>
                    <div className={styles.modalTitle}>Are you sure you want to remove this variant?</div>
                    <div className={styles.modalActions}>
                        <Button className={styles.modalPrimaryBtn} size={"lg"} onButtonClick={cancelRemove}>Don&apos;t remove</Button>
                        <Button className={styles.modalSecondaryBtn} size={"lg"} onButtonClick={confirmRemove}>Yes, Remove</Button>
                    </div>
                </div>
            </ModalManager>
            <AddToCompareModal isOpen={openMMVModal} onClose={() => setOpenMMVModal(false)} title={'Add a car to compare'} cityName={cityName} handleAddToCompare={handleMMVSelection} />
            {compareModelsList && availableVariants?.length > 0 && modelsData?.length > 0 && <div className={styles.comparisonWrapper}>
                <div className={styles.header}>
                    <div className={styles.titleSection}>
                        <h1 className={styles.subTitle}>{header}</h1>
                    </div>
                    <div className={styles.headerActions}>
                        {!isMobile && <div className={styles.diffSwitchToggle}>
                            <ToggleSwitch
                                isOn={isOnlyDiff}
                                onToggle={handleDiffFilter}
                            />
                            Show only the differences
                        </div>}
                        {compareModelsList?.length < MAX_COMPARISON_CARDS && (
                            <button className={styles.addButton} onClick={onAddClick}>
                                <ImageAtom
                                    src={`${assetLoader()}/icons/Add_darker.svg`}
                                    alt="Add"
                                    width={12}
                                    height={12}
                                    className={styles.icon}
                                />
                               {isMobile ? 'Add' : 'Add car'}
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.compareCardsWrapper}>
                    {modelsData.map((item: IModelsData, index: number) => (
                        <CompareCard
                            key={index}
                            make={item.brand.name}
                            model={item.model.name}
                            image={`${s3UrlLoader()}/${toKebabCase(item.brand.name)}/${toKebabCase(item.model.name)}/default/Hero-Transparent.png`}
                            variants={availableVariants[index]}
                            selectedVariant={selectedVariants[index]}
                            onVariantChange={(newVariant) => handleVariantChange(index, newVariant)}
                            onRemove={() => onRemoveClick(index)}
                            onEdit={() => onEdit(index)}
                            index={index}
                            slug={item.model.slug}
                            isSellable={item.status !== "out_of_production" && item.status !== "non_sellable"}
                        />
                    ))}
                </div>
                {isMobile && <div className={styles.diffSwitchToggle}>
                    <ToggleSwitch
                        isOn={isOnlyDiff}
                        onToggle={handleDiffFilter}
                    />
                    Show only the differences
                </div>}
                <div className={styles.priceBlock}>
                    {selectedVariants?.map((variant: CarVariant, index) => (
                        <ComparePriceCard
                            key={index}
                            adspPrice={variant?.adspPrice || 0}
                            onRoadPrice={variant?.onRoadPrice || 0}
                            ackoDriveSavings={variant?.ackoDriveSavings || 0}
                            cityName={cityName}
                            isLoggedIn={isLoggedIn}
                            isMobile={isMobile}
                        />
                    ))}
                </div>
                <div className={styles.accordionContainer}>
                    {filteredData?.map((section) => (
                        <div key={section.category} className={styles.accordion}>
                            <div
                                className={styles.accordionHeader}
                                onClick={() => toggleAccordion(section.category)}
                            >
                                <AccentBar isActive={expanded[section.category]} />
                                <h2 className={styles.accordionCategory}>{section.category}</h2>
                                <ImageAtom
                                    src={`${assetLoader()}${expanded[section.category] ? `/up-arrow.svg` : `/down-arrow.svg`}`}
                                    alt={expanded[section.category] ? "Collapse" : "Expand"}
                                    width={24}
                                    height={24}
                                />
                            </div>
                            {expanded[section.category] && (
                                <div className={styles.accordionContent}>
                                    <div className={styles.featureBlock}>
                                        {section.features.map((feature) => (
                                            <div className={styles.featureRow} key={feature.feature}>
                                                <div className={styles.featureTitle}>{feature.feature}</div>
                                                <div className={styles.featureValueContainer}>
                                                    {Object.entries(feature.values).map(([variant, value]) => {
                                                        return (
                                                          <div
                                                            key={variant}
                                                            className={
                                                              styles.featureValue
                                                            }
                                                          >
                                                            {!value ||
                                                            (typeof value ===
                                                              "string" &&
                                                              value.trim()
                                                                .length === 0)
                                                              ? "-"
                                                              : value}
                                                          </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>}
        </>
    );
}